// Media_Abastecimento.jsx
import React from 'react';
import { Paper, Typography, Box, TextField, InputAdornment } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';
import { useThemeMode } from '../context/ThemeContext';

const API_BASE =
  (typeof import.meta !== 'undefined' &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  '';

const API_KEY =
  (typeof import.meta !== 'undefined' &&
    import.meta.env &&
    import.meta.env.VITE_API_KEY) ||
  process.env.REACT_APP_API_KEY ||
  '';

/**
 * Converte qualquer valor em número (aceita "2,37", "1.234,56", etc.)
 */
function toNumber(value) {
  if (value == null) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  let s = String(value).trim();
  if (!s) return 0;

  // troca vírgula por ponto
  s = s.replace(/\./g, '').replace(',', '.');
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

// Formatter compatível com versões do DataGrid que chamam valueFormatter(value) ou valueFormatter(params)
function formatPtBr2(paramsOrValue) {
  const raw =
    paramsOrValue && typeof paramsOrValue === 'object' && 'value' in paramsOrValue
      ? paramsOrValue.value
      : paramsOrValue;

  const v = toNumber(raw);
  if (!Number.isFinite(v) || v === 0) return '0,00';
  return v.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Tabela "KM MÉDIA DOS VEÍCULOS POR DIA"
 * baseYmd = data em YYYY-MM-DD que define o mês
 */
export default function MediaAbastecimento({ baseYmd }) {
  const { mode } = useThemeMode();

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [columns, setColumns] = React.useState([]);

  const [filtroVeiculo, setFiltroVeiculo] = React.useState('');

  const filteredRows = React.useMemo(() => {
    const q = String(filtroVeiculo ?? '').trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) =>
      String(r.veiculo ?? '').toLowerCase().includes(q)
    );
  }, [rows, filtroVeiculo]);

  React.useEffect(() => {
    const ac = new AbortController();

    async function load() {
      if (!baseYmd) {
        setRows([]);
        setColumns([]);
        setError('');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        // Sempre usa o mês inteiro do baseYmd
        const ref = dayjs(baseYmd, 'YYYY-MM-DD');
        const startMonthBR = ref.startOf('month').format('DD/MM/YYYY');
        const endMonthBR = ref.endOf('month').format('DD/MM/YYYY');

        const sp = new URLSearchParams();
        sp.set('startDate', startMonthBR);
        sp.set('endDate', endMonthBR);

        const res = await fetch(
          `${API_BASE}/api/setor3/media-abastecimento?${sp.toString()}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: API_KEY,
              'x-api-key': API_KEY,
            },
            signal: ac.signal,
          }
        );

        if (!res.ok) {
          throw new Error(
            `Erro ao buscar média de abastecimento (HTTP ${res.status})`
          );
        }

        const data = await res.json();
        const matriz = Array.isArray(data.matriz) ? data.matriz : [];

        // ==== 1) Agrupa por veículo e descobre dias ====
        const diasSet = new Set();            // dias existentes (1..31)
        const porVeiculo = new Map();         // veiculo -> { veiculo, porDia: Map, soma, count }

        for (const item of matriz) {
          if (!item) continue;

          const veiculo = String(item.veiculo ?? '').trim() || 'N/I';
          const dia = Number(item.dia);
          if (!Number.isFinite(dia) || dia < 1 || dia > 31) continue;

          diasSet.add(dia);

          const kmMedia = toNumber(item.kmMediaPorLitro);

          let agg = porVeiculo.get(veiculo);
          if (!agg) {
            agg = {
              veiculo,
              porDia: new Map(), // dia -> kmMedia
              soma: 0,
              count: 0,
            };
            porVeiculo.set(veiculo, agg);
          }

          // registra a média daquele dia
          agg.porDia.set(dia, kmMedia);

          // acumula para média geral (considerando apenas > 0)
          if (kmMedia > 0) {
            agg.soma += kmMedia;
            agg.count += 1;
          }
        }

        const diasOrdenados = Array.from(diasSet).sort((a, b) => a - b);

        // ==== 2) Monta linhas do DataGrid ====
        const dgRows = Array.from(porVeiculo.values()).map((agg, idx) => {
          const row = {
            id: `${agg.veiculo}-${idx}`,
            veiculo: agg.veiculo,
          };

          for (const dia of diasOrdenados) {
            const valorDia = agg.porDia.get(dia);
            const v = toNumber(valorDia);
            row[`dia_${dia}`] = v;
          }

          const mediaGeral =
            agg.count > 0 ? agg.soma / agg.count : 0;
          row.mediaGeral = mediaGeral;

          return row;
        });

        // ==== 3) Define colunas ====
        const baseColumns = [
          {
            field: 'veiculo',
            headerName: 'VEÍCULO',
            width: 110,
            headerAlign: 'center',
            align: 'center',
          },
        ];

        const diaColumns = diasOrdenados.map((dia) => ({
          field: `dia_${dia}`,
          headerName: String(dia),
          width: 80,
          headerAlign: 'center',
          align: 'center',
          type: 'number',
          valueFormatter: formatPtBr2,
        }));

        const mediaColumn = {
          field: 'mediaGeral',
          headerName: 'MÉDIA GERAL',
          width: 120,
          headerAlign: 'center',
          align: 'center',
          type: 'number',
          valueFormatter: formatPtBr2,
        };

        setColumns([...baseColumns, ...diaColumns, mediaColumn]);
        setRows(dgRows);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.error('Erro média abastecimento (tabela):', err);
        setError(err.message || String(err));
        setRows([]);
        setColumns([]);
      } finally {
        setLoading(false);
      }
    }

    load();
    return () => ac.abort();
  }, [baseYmd]);

  return (
    <Paper sx={{ mt: 2, p: { xs: 1.5, sm: 2 }, borderRadius: 2, width: '100%' }}>
      <Typography
        variant="h6"
        sx={{ mb: 1, fontWeight: 600, textAlign: 'center' }}
      >
        KM MÉDIA DOS VEÍCULOS POR DIA
      </Typography>

      {error && (
        <Typography
          variant="body2"
          color="error"
          sx={{ mb: 1, textAlign: 'center' }}
        >
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
        <TextField
          size="small"
          label="Pesquisar Veículo"
          placeholder="Ex: 00000"
          value={filtroVeiculo}
          onChange={(e) => setFiltroVeiculo(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: filtroVeiculo ? (
              <InputAdornment position="end">
                <Box
                  component="button"
                  type="button"
                  onClick={() => setFiltroVeiculo('')}
                  aria-label="Limpar filtro"
                  style={{
                    background: 'transparent',
                    border: 0,
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontSize: 18,
                      lineHeight: 1,
                      color: mode === 'dark' ? '#cfd8dc' : '#6b7280',
                      px: 0.5,
                    }}
                  >
                    ×
                  </Box>
                </Box>
              </InputAdornment>
            ) : null,
          }}

          sx={{
            width: { xs: '100%', sm: 220 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: mode === 'dark' ? '#0b0e10' : '#fff',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? '#1f2a30' : '#cbd5e1',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? '#2f3b42' : '#94a3b8',
            },
            '& .MuiInputLabel-root': {
              color: mode === 'dark' ? '#cfd8dc' : '#64748b',
            },
            '& .MuiOutlinedInput-input': {
              color: mode === 'dark' ? '#fff' : '#0f172a',
            },
            '& .MuiSvgIcon-root': {
              color: mode === 'dark' ? '#cfd8dc' : '#6b7280',
            },
          }}
        />
      </Box>

      <Box
        sx={{
          height: 400,
          width: '100%',
          '& .MuiDataGrid-root': {
            backgroundColor: mode === 'dark' ? '#0b0e10' : '#ffffff',
            color: mode === 'dark' ? '#e8e8e8' : '#111',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: mode === 'dark' ? '#0b0e10' : '#E1E1E2',
            borderBottom: `1px solid ${
              mode === 'dark' ? '#E1F7D5' : '#E0E0E0'
            }`,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            color: mode === 'dark' ? '#e8e8e8' : '#4D4D4D',
            fontWeight: 600,
            textTransform: 'uppercase',
            textAlign: 'center',
            width: '100%',
          },
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            justifyContent: 'center',
          },
          '& .MuiDataGrid-iconButtonContainer .MuiSvgIcon-root, & .MuiDataGrid-sortIcon': {
            color: mode === 'dark' ? '#cfd8dc' : '#6b7280',
          },
          '& .MuiDataGrid-row': {
            backgroundColor: mode === 'dark' ? '#0b0e10' : '#ffffff',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${
              mode === 'dark' ? '#1f2a30' : '#E0E0E0'
            }`,
            alignItems: 'center',
            justifyContent: 'center',
          },
          '& .MuiDataGrid-cellContent': {
            textAlign: 'center',
            width: '100%',
          },
        }}
      >
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          rowHeight={44}
          disableRowSelectionOnClick
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          localeText={{ noRowsLabel: 'Sem linhas' }}
        />
      </Box>
    </Paper>
  );
}
